/* Surfstr — homepage scroll-reveal engine.
   Self-contained, zero dependencies, defensive (never throws on storefront).

   Progressive enhancement contract:
   - Default (no JS): every element stays in its natural visible state.
   - prefers-reduced-motion / no IntersectionObserver: we bail early WITHOUT
     adding the `surfstr-anim-ready` flag, so the hidden pre-state in
     surfstr-animations.css is never applied → all content visible immediately.
   - Otherwise: tag targeted elements, hide them via CSS, then fade + gentle
     slide-up (translateY only) once as they enter the viewport.

   Only opacity/transform are animated (GPU-friendly). Observers disconnect
   after each element reveals; will-change is cleared once settled. */
(function () {
  'use strict';

  var REVEAL_ATTR = 'data-surfstr-reveal';
  var IN_CLASS = 'is-revealed';
  var STAGGER_CAP = 420; // ms — keep grids snappy even when long
  var SETTLE_MS = 900; // ms — clear will-change a touch after the transition

  function prefersReducedMotion() {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      return false;
    }
  }

  // Bail safely → content remains visible via default CSS.
  if (prefersReducedMotion() || !('IntersectionObserver' in window)) return;

  function init() {
    try {
      run();
    } catch (e) {
      /* never break the storefront */
    }
  }

  function run() {
    var docEl = document.documentElement;
    docEl.classList.add('surfstr-anim-ready');

    var observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var entry = entries[i];
          if (!entry.isIntersecting) continue;
          reveal(entry.target);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );

    function reveal(el) {
      if (!el || el.classList.contains(IN_CLASS)) return;
      el.classList.add(IN_CLASS);
      window.setTimeout(function () {
        el.style.willChange = 'auto';
      }, SETTLE_MS);
    }

    // Tag a single element for reveal. `variant` maps to the CSS distance
    // tiers ('' default, 'card', 'soft'); `delay` adds stagger via inline
    // transition-delay (transform/opacity only — no layout impact).
    function tag(el, variant, delay) {
      if (!el || el.hasAttribute(REVEAL_ATTR)) return;
      el.setAttribute(REVEAL_ATTR, variant || '');
      if (delay) el.style.transitionDelay = delay + 'ms';
      observer.observe(el);
    }

    function tagSingles(selector, variant) {
      var nodes = document.querySelectorAll(selector);
      for (var i = 0; i < nodes.length; i++) tag(nodes[i], variant, 0);
    }

    // Stagger siblings within each shared parent (grids / lists). Document
    // order keeps siblings contiguous, so resetting the index on parent change
    // staggers each group independently.
    function tagStagger(selector, variant, step) {
      var nodes = document.querySelectorAll(selector);
      var lastParent = null;
      var idx = 0;
      for (var i = 0; i < nodes.length; i++) {
        var node = nodes[i];
        if (node.parentNode !== lastParent) {
          lastParent = node.parentNode;
          idx = 0;
        }
        var delay = idx * step;
        if (delay > STAGGER_CAP) delay = STAGGER_CAP;
        tag(node, variant, delay);
        idx++;
      }
    }

    /* --- Hero: gentle intro on load (above the fold → reveals immediately) -- */
    tag(document.querySelector('.surfstr-lovable-hero__title'), 'soft', 0);
    tag(document.querySelector('.surfstr-lovable-hero__sub'), 'soft', 90);
    tag(document.querySelector('.surfstr-lovable-hero__actions'), 'soft', 170);
    tag(document.querySelector('.surfstr-lovable-hero__media-wrap'), '', 80);

    /* --- Section headings + media singles ---------------------------------- */
    tagSingles('.surfstr-benefits__header', 'soft');
    tagSingles('.surfstr-band__title', 'soft');
    tagSingles('.surfstr-band__body', '');
    tagSingles('.surfstr-band__media', '');
    tagSingles('.surfstr-video-reviews__intro', 'soft');
    tagSingles('.surfstr-shipping__header', 'soft');
    tagSingles('.surfstr-shipping__media', '');
    tagSingles('.surfstr-shipping-graphic', '');
    tagSingles('.surfstr-final-cta__card', '');
    tagSingles('.surfstr-faq__heading', 'soft');

    /* --- Staggered grids / lists ------------------------------------------- */
    tagStagger('.surfstr-benefits__card', 'card', 80);
    tagStagger('.surfstr-faq__item', '', 70);

    /* --- Video review cards (injected async by surfstr-reviews.js) ---------
       The cards live in a horizontal carousel, so per-card viewport
       intersection would leave off-screen-right cards hidden. Instead we
       reveal the whole group (staggered) once the carousel enters the vertical
       viewport — and reveal any later-injected cards immediately once in view.
       Cards never stay hidden regardless of horizontal scroll position. */
    watchVideoCards(reveal);
  }

  function watchVideoCards(reveal) {
    var carousels = document.querySelectorAll('.surfstr-video-reviews__carousel');
    if (!carousels.length) return;

    for (var c = 0; c < carousels.length; c++) {
      (function (carousel) {
        var inView = false;

        function syncCards() {
          var cards = carousel.querySelectorAll('.surfstr-video-reviews__card');
          for (var i = 0; i < cards.length; i++) {
            var card = cards[i];
            if (!card.hasAttribute(REVEAL_ATTR)) {
              card.setAttribute(REVEAL_ATTR, 'card');
              var delay = i * 70;
              if (delay > STAGGER_CAP) delay = STAGGER_CAP;
              card.style.transitionDelay = delay + 'ms';
            }
            if (inView) reveal(card);
          }
        }

        var io = new IntersectionObserver(
          function (entries) {
            for (var i = 0; i < entries.length; i++) {
              if (!entries[i].isIntersecting) continue;
              inView = true;
              io.disconnect();
              syncCards();
              break;
            }
          },
          { threshold: 0.1, rootMargin: '0px 0px -8% 0px' }
        );
        io.observe(carousel);

        syncCards();

        var track = carousel.querySelector('.surfstr-video-reviews__track[data-track]') || carousel;
        if ('MutationObserver' in window) {
          var mo = new MutationObserver(syncCards);
          mo.observe(track, { childList: true });
        }
      })(carousels[c]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* Homepage i18n sync bootstrap — runs even when cached layout omits the dedicated script tag. */
(function loadHomeI18nSync() {
  if (!document.body || !document.body.classList.contains('surfstr-lovable-layout')) return;
  if (document.documentElement.getAttribute('data-surfstr-home-i18n-synced') === 'true') return;
  if (document.querySelector('script[src*="surfstr-home-i18n-sync.js"]')) return;

  var baseScript = document.querySelector('script[src*="surfstr-animations.js"]');
  if (!baseScript || !baseScript.src) return;

  var syncSrc = baseScript.src.replace(/surfstr-animations\.js(\?[^#]*)?/, 'surfstr-home-i18n-sync.js$1');
  var syncScript = document.createElement('script');
  syncScript.src = syncSrc;
  syncScript.defer = true;
  document.head.appendChild(syncScript);
})();
